import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { api, type StaffLeg, type StaffSession, type StaffSessions } from '../../api/client';
import { Button, Card, Chip, Empty, LoadError, Loading, Notice, PageHeader } from '../../components/ui';
import { durationText } from '../../lib/patchText';
import { useData } from '../../lib/useData';

/**
 * Stitch Admin → a customer → Live sessions (1.13.0, Teampace 3126, proposal 78): the company's calls
 * in the app and Patches, newest first, each with its legs (who was rung, who answered, on which
 * device) and how each ended. Search by person, extension or number, and by time. So "the desk
 * phone didn't ring" is a lookup. Read-only: it shows what Live sessions keeps, and records nothing.
 */

/** "Nov 2, 2:00:05 PM": seconds, since these are lined up against logs. In this browser's time. */
const when = (iso: string | null) => (iso ? new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit' }).format(new Date(iso)) : '–');

const ORIGIN: Record<string, string> = { incoming: 'Call in', dialed: 'Call out', dm: 'Patch from a direct message', channel: 'Patch in a channel' };
const KIND: Record<StaffSession['kind'], string> = { call: 'Call', patch: 'Patch', meeting: 'Meeting' };
const LEG_STATE: Record<StaffLeg['state'], { label: string; tone: 'ok' | 'warn' | 'neutral' | 'brand' }> = {
  ringing: { label: 'Ringing', tone: 'brand' },
  active: { label: 'On it', tone: 'ok' },
  held: { label: 'On hold', tone: 'neutral' },
  left: { label: 'Left', tone: 'neutral' },
  missed: { label: 'Missed', tone: 'warn' },
  declined: { label: 'Declined', tone: 'warn' }
};

/** A <input type="datetime-local"> value as an ISO time, in this browser's zone. */
const isoOf = (local: string) => (local ? new Date(local).toISOString() : '');

export function AdminCustomerSessions() {
  const { id = '' } = useParams();
  const [form, setForm] = useState({ q: '', kind: '' as '' | 'call' | 'patch', from: '', to: '' });
  const [query, setQuery] = useState(form);
  const page = useData(() => api.customerSessions(id, { q: query.q, kind: query.kind || undefined, from: isoOf(query.from), to: isoOf(query.to) }), [id, query]);
  const [older, setOlder] = useState<StaffSessions['sessions']>([]);
  const [next, setNext] = useState<string | null | undefined>(undefined);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState('');

  const search = (e: FormEvent) => {
    e.preventDefault();
    setOlder([]);
    setNext(undefined);
    setQuery({ ...form });
  };
  const after = next === undefined ? (page.data?.next ?? null) : next;

  async function loadMore() {
    if (!after) return;
    setLoadingMore(true);
    setMoreError('');
    try {
      const r = await api.customerSessions(id, { q: query.q, kind: query.kind || undefined, from: isoOf(query.from), to: isoOf(query.to), before: after });
      setOlder((x) => [...x, ...r.sessions]);
      setNext(r.next);
    } catch (e) {
      setMoreError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoadingMore(false);
    }
  }

  const c = page.data?.customer;
  const list = [...(page.data?.sessions ?? []), ...older];
  return (
    <>
      <PageHeader
        eyebrow={
          <>
            <Link to="/admin/customers">Customers</Link> · <Link to={`/admin/customers/${encodeURIComponent(id)}`}>{c?.name ?? 'Customer'}</Link>
          </>
        }
        title="Live sessions"
      />
      <div className="content">
        <p className="muted">
          Calls in the app and Patches, newest first: who was rung, who answered and on which device, and how each ended. Times are in this browser’s time zone. Read only:
          nothing here records or listens.
        </p>
        <Card>
          <form className="card-body sessions-search" onSubmit={search} role="search" aria-label="Search live sessions">
            <div className="field grow">
              <label htmlFor="ls-q">Person, extension or number</label>
              <input id="ls-q" className="input" value={form.q} placeholder="Dana, 201 or (410) 555-0100" onChange={(e) => setForm({ ...form, q: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="ls-kind">What</label>
              <select id="ls-kind" className="input" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value as '' | 'call' | 'patch' })}>
                <option value="">Calls and Patches</option>
                <option value="call">Calls</option>
                <option value="patch">Patches</option>
              </select>
            </div>
            <div className="field">
              <label htmlFor="ls-from">From</label>
              <input id="ls-from" className="input" type="datetime-local" value={form.from} onChange={(e) => setForm({ ...form, from: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="ls-to">To</label>
              <input id="ls-to" className="input" type="datetime-local" value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })} />
            </div>
            <Button type="submit" variant="primary" icon="search">
              Search
            </Button>
          </form>
        </Card>
        {page.error ? (
          <Card>
            <LoadError error={page.error} retry={page.reload} />
          </Card>
        ) : !page.data ? (
          <Loading label="Reading live sessions" />
        ) : list.length === 0 ? (
          <Card>
            <Empty title={query.q || query.from || query.to || query.kind ? 'Nothing matches' : 'No calls or Patches yet'}>
              {query.q || query.from || query.to || query.kind ? 'Try another name, number or time.' : `Calls in the app and Patches at ${c?.name ?? 'this company'} show here as they happen.`}
            </Empty>
          </Card>
        ) : (
          <ol className="plain-list session-list" aria-label="Sessions">
            {list.map((s) => (
              <SessionCard key={s.id} s={s} />
            ))}
          </ol>
        )}
        {moreError && <Notice tone="crit">{moreError}</Notice>}
        {after && list.length > 0 && (
          <Button onClick={() => void loadMore()} disabled={loadingMore}>
            {loadingMore ? 'Loading…' : 'Older sessions'}
          </Button>
        )}
      </div>
    </>
  );
}

function SessionCard({ s }: { s: StaffSession }) {
  const title = ORIGIN[s.origin] ?? KIND[s.kind];
  const going = s.state !== 'ended';
  return (
    <li>
      <Card
        title={`${title} · ${when(s.startedAt)}`}
        action={
          going ? (
            <Chip tone="ok">{s.state === 'ringing' ? 'Ringing now' : 'Going now'}</Chip>
          ) : (
            <span className="muted">
              {s.seconds !== null ? durationText(s.seconds) : 'Not answered'}
              {s.ended ? ` · ${s.ended}` : ''}
            </span>
          )
        }
        foot={
          <span className="muted small">
            {s.answeredAt ? `Answered ${when(s.answeredAt)}` : 'Never answered'}
            {s.endedAt ? ` · ended ${when(s.endedAt)}` : ''}
            {s.endCause && s.ended !== s.endCause ? ` · ${s.endCause}` : ''} · <span className="mono">{s.id}</span>
          </span>
        }
      >
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Who</th>
                <th scope="col">On</th>
                <th scope="col">What happened</th>
                <th scope="col">Joined</th>
                <th scope="col">Left</th>
                <th scope="col">How it ended</th>
              </tr>
            </thead>
            <tbody>
              {s.legs.map((l) => (
                <tr key={l.key}>
                  <td>
                    {l.who}
                    {l.extension && l.kind !== 'outside' && <small className="muted"> · Ext {l.extension}</small>}
                    {l.number && l.kind === 'outside' && l.who !== l.number && <small className="muted mono"> {l.number}</small>}
                  </td>
                  <td>
                    {l.device}
                    {l.via && <small className="muted"> · {l.via === 'webrtc' ? 'in the browser or app' : 'through the call gateway'}</small>}
                  </td>
                  <td>
                    <Chip small tone={LEG_STATE[l.state].tone}>
                      {LEG_STATE[l.state].label}
                    </Chip>
                  </td>
                  <td className="nowrap">{when(l.joinedAt)}</td>
                  <td className="nowrap">{when(l.leftAt)}</td>
                  <td>
                    {l.ended || '–'}
                    {l.cause && l.ended !== l.cause && <small className="muted mono"> {l.cause}</small>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </li>
  );
}
